/**
 * Infographic generation: dispatcher + poller for the async background job
 * workflow. POST /content-generation/infographic/generate returns HTTP 202
 * with status="pending"; the final S3 URL surfaces only via
 * GET /content-generation/infographic/status/{post_id}. This module keeps
 * that orchestration framework-agnostic so it can be unit-tested against a
 * mocked API client.
 */
import type {
  InfographicAlreadyGeneratedResponse,
  InfographicGenerateRequest,
  InfographicGenerateResponse,
  ImageOption,
} from '../types/infographic';

export const INFOGRAPHIC_POLL_INTERVAL_MS = 4000;
export const INFOGRAPHIC_POLL_TIMEOUT_MS = 5 * 60 * 1000;
export const INFOGRAPHIC_MAX_CONSECUTIVE_ERRORS = 3;

/** Minimal surface of the API client this service needs. */
export interface InfographicApiClient {
  generateInfographic(
    request: InfographicGenerateRequest
  ): Promise<InfographicGenerateResponse | InfographicAlreadyGeneratedResponse>;
  getInfographicStatus(postId: string): Promise<InfographicGenerateResponse>;
}

export interface DispatchResult {
  /** Resolved post_id the background job runs against. */
  postId: string;
  /** Raw POST response — status is expected to be "pending" on 202. */
  response: InfographicGenerateResponse | InfographicAlreadyGeneratedResponse;
}

/**
 * Dispatch the generation job. Resolves once the POST returns (202), i.e. the
 * job has been accepted and runs in the background — it does NOT wait for the
 * image. Throws on transport/HTTP errors so callers can surface a message.
 */
export async function dispatchInfographicGeneration(
  api: InfographicApiClient,
  request: InfographicGenerateRequest
): Promise<DispatchResult> {
  const response = await api.generateInfographic(request);
  const postId = response && 'post_id' in response ? response.post_id : request.post_id;
  return { postId, response };
}

export interface PollInfographicStatusOptions {
  postId: string;
  /** Seconds-ish cadence between status checks. Defaults to 4s. */
  intervalMs?: number;
  /** Max wait before giving up and firing onTimeout. Defaults to 5 min. */
  timeoutMs?: number;
  /** Consecutive HTTP/network errors tolerated before onError. Defaults to 3. */
  maxConsecutiveErrors?: number;
  /** Fired on every successful status read (useful for debug/tests). */
  onStatus?: (status: InfographicGenerateResponse) => void;
  /**
   * status === "done" with a non-empty output_paths[0]. imageOptions/
   * selectedImageIndex are set only when the quality gate exhausted every
   * attempt — a caveat on an otherwise-done job, not an error.
   */
  onDone: (imageUrl: string, imageOptions?: ImageOption[] | null, selectedImageIndex?: number | null) => void;
  /** status === "error", or too many consecutive polling errors. */
  onError: (message: string) => void;
  /** Polled for the full timeout window without a terminal state. */
  onTimeout: () => void;
}

/**
 * Poll the status endpoint until the job reaches a terminal state, the
 * timeout elapses, or cancellation. Returns a cancel() handle.
 *
 * Stopping semantics:
 * - done  -> onDone(url), stop
 * - error -> onError(message), stop (credits already auto-refunded)
 * - timeout -> onTimeout(), stop (surfaces a "taking longer than expected"
 *   notice; user may retry explicitly)
 * - 404/422/network -> up to maxConsecutiveErrors transient retries, then
 *   onError and stop (no infinite loop)
 */
export function pollInfographicStatus(
  api: InfographicApiClient,
  options: PollInfographicStatusOptions
): () => void {
  const {
    postId,
    intervalMs = INFOGRAPHIC_POLL_INTERVAL_MS,
    timeoutMs = INFOGRAPHIC_POLL_TIMEOUT_MS,
    maxConsecutiveErrors = INFOGRAPHIC_MAX_CONSECUTIVE_ERRORS,
    onStatus,
    onDone,
    onError,
    onTimeout,
  } = options;

  let cancelled = false;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let consecutiveErrors = 0;
  const startedAt = Date.now();

  const stop = () => {
    cancelled = true;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const isExpired = () => Date.now() - startedAt >= timeoutMs;

  const scheduleNext = () => {
    if (cancelled) return;
    timer = setTimeout(() => { void tick(); }, intervalMs);
  };

  const tick = async () => {
    if (cancelled) return;
    if (isExpired()) {
      onTimeout();
      stop();
      return;
    }
    try {
      const status = await api.getInfographicStatus(postId);
      if (cancelled) return;
      consecutiveErrors = 0;
      onStatus?.(status);

      if (status.status === 'done') {
        const imageUrl = status.output_paths?.[0];
        if (imageUrl) {
          onDone(imageUrl, status.image_options, status.selected_image_index);
        } else {
          onError('Generation completed, but no image URL was returned.');
        }
        stop();
        return;
      }

      if (status.status === 'error') {
        onError(status.error_message || 'Generation failed. Your credits were refunded automatically.');
        stop();
        return;
      }

      // status === "pending" — worker still running
      scheduleNext();
    } catch {
      if (cancelled) return;
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        onError(
          'Could not check the generation status. Please try again in a moment.'
        );
        stop();
        return;
      }
      scheduleNext();
    }
  };

  void tick();
  return stop;
}